
import { useState } from 'react';
import { CreateFarmPriceInput } from '@/types/database/farm-prices';
import { farmPriceService } from '@/services/farmPriceService';

interface UploadProgress {
  current: number;
  total: number;
  failed: number;
  stage: 'uploading' | 'complete' | 'failed';
}

interface UploadResult {
  successful: number;
  failed: number;
  errors: string[];
}

export const useBulkUploadProgress = () => {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<UploadProgress>({ 
    current: 0, 
    total: 0, 
    failed: 0, 
    stage: 'uploading' 
  });
  const [uploadResult, setUploadResult] = useState<UploadResult | null>(null);

  const uploadWithProgress = async (prices: CreateFarmPriceInput[]): Promise<UploadResult> => {
    setIsUploading(true);
    setUploadProgress({ current: 0, total: prices.length, failed: 0, stage: 'uploading' });
    setUploadResult(null);

    const batchSize = 20; // Upload in smaller batches to track progress
    let successful = 0;
    let failed = 0;
    const errors: string[] = [];

    try {
      for (let i = 0; i < prices.length; i += batchSize) {
        const batch = prices.slice(i, i + batchSize);
        
        try {
          await farmPriceService.bulkCreateFarmPrices(batch);
          successful += batch.length;
        } catch (error) {
          failed += batch.length;
          const errorMessage = error instanceof Error ? error.message : 'Unknown error';
          errors.push(`Batch ${Math.floor(i / batchSize) + 1}: ${errorMessage}`);
          console.error(`Error uploading batch starting at index ${i}:`, error);
        }

        // Update progress
        setUploadProgress({
          current: i + batch.length,
          total: prices.length,
          failed,
          stage: 'uploading'
        });

        // Small delay to allow UI updates
        await new Promise(resolve => setTimeout(resolve, 100));
      }

      const result: UploadResult = { successful, failed, errors };
      setUploadResult(result);
      
      setUploadProgress(prev => ({
        ...prev,
        stage: failed > 0 ? 'failed' : 'complete'
      }));

      return result;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Upload failed';
      const result: UploadResult = { successful: 0, failed: prices.length, errors: [errorMessage] };
      setUploadResult(result);
      
      setUploadProgress(prev => ({
        ...prev,
        stage: 'failed'
      }));

      throw error;
    } finally {
      setIsUploading(false);
    }
  };

  const resetUpload = () => {
    setUploadProgress({ current: 0, total: 0, failed: 0, stage: 'uploading' });
    setUploadResult(null);
  };

  return {
    uploadWithProgress,
    isUploading,
    uploadProgress,
    uploadResult,
    resetUpload
  };
};
